
export interface TableData {
  headers: string[];
  rows: string[][];
  isTable: boolean;
}

export const parseMarkdownTable = (text: string): TableData | null => {
  const lines = text.split('\n');
  
  // Find the separator line with stronger validation
  // Must have | and at least 3 consecutive dashes between pipes
  const separatorIndex = lines.findIndex(line => {
    const trimmed = line.trim();
    if (!trimmed.includes('|') || !trimmed.includes('-')) return false;
    
    // Split by | and check each part for dashes
    const parts = trimmed.split('|').map(p => p.trim());
    const dashParts = parts.filter(p => /^-{3,}$/.test(p));
    
    // Need at least 2 columns with proper dash separators
    return dashParts.length >= 2;
  });
  
  if (separatorIndex === -1 || separatorIndex === 0) {
    return null;
  }
  
  // Header should be the line immediately before the separator
  const headerLine = lines[separatorIndex - 1];
  if (!headerLine || !headerLine.includes('|')) {
    return null;
  }
  
  // Validate header doesn't look like a separator
  if (headerLine.trim().match(/^[\|\s-]+$/)) {
    return null;
  }
  
  const headers = headerLine
    .split('|')
    .map(cell => cell.trim())
    .filter(cell => cell !== '');
  
  if (headers.length === 0) {
    return null;
  }
  
  // Extract only the data rows that come AFTER the separator
  const dataLines = lines.slice(separatorIndex + 1);
  const rows = dataLines
    .filter(line => {
      const trimmed = line.trim();
      // Must have | and not be just dashes/spaces
      return trimmed.includes('|') && 
             trimmed.length > 0 && 
             !trimmed.match(/^[\|\s-]+$/);
    })
    .map(line => 
      line
        .split('|')
        .map(cell => cell.trim())
        .filter(cell => cell !== '')
    )
    .filter(row => {
      // Must match header length and not be a duplicate header row
      if (row.length !== headers.length) return false;
      
      // Filter out rows that exactly match headers (prevents duplicate headers)
      const isHeaderDuplicate = row.every((cell, i) => 
        cell.toLowerCase() === headers[i].toLowerCase()
      );
      
      return !isHeaderDuplicate;
    });
  
  if (rows.length === 0) {
    return null;
  }
  
  return {
    headers,
    rows,
    isTable: true
  };
};

export const formatCellValue = (value: string, header: string): string => {
  // Format currency values
  if (header.toLowerCase().includes('price') || header.toLowerCase().includes('cost')) {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  }
  
  // Format large numbers
  const num = parseFloat(value);
  if (!isNaN(num) && num >= 1000) {
    return num.toLocaleString('en-US');
  }
  
  return value;
};
